import * as Sentry from '@sentry/react';

const SENTRY_DSN = 'https://d4750c076a4b7b7350bd532633ed4c30@o4510575872835584.ingest.us.sentry.io/4510632762998784';

export function initSentry() {
  if (import.meta.env.DEV) {
    console.log('Sentry disabled in development mode');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: '0.1.0',

    // Performance monitoring
    tracesSampleRate: 1.0,

    // Session Replay - capture all sessions in staging, 10% in production
    replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0, // Always capture on error

    integrations: [
      // Session Replay with comprehensive settings
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // Capture console logs in replay
        networkDetailAllowUrls: [
          import.meta.env.VITE_API_URL,
          /^https:\/\/wpcruddly.*\.cruddly\.com/,
        ],
        // Capture request/response bodies for debugging
        networkRequestHeaders: ['Content-Type', 'Authorization'],
        networkResponseHeaders: ['Content-Type'],
      }),

      // Browser tracing for performance
      Sentry.browserTracingIntegration({
        // Track navigation and page loads
        enableInp: true,
      }),

      // Capture unhandled promise rejections
      Sentry.captureConsoleIntegration({
        levels: ['error', 'warn'],
      }),

      // HTTP client errors
      Sentry.httpClientIntegration(),

      // Report web vitals
      Sentry.browserProfilingIntegration(),
    ],

    // Filter out non-actionable errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Ignore network errors that are likely user connectivity issues
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        // Still capture but with lower priority
        event.level = 'warning';
      }

      // Scrub sensitive data
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
      }

      return event;
    },

    // Ignore certain URLs (browser extensions, etc.)
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });

  // Set up global context
  Sentry.setContext('app', {
    name: 'WP Cruddly',
    version: '0.1.0',
  });
}

// Set user context when user logs in
export function setUser(user: { id: string; email: string; name?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
}

// Clear user context on logout
export function clearUser() {
  Sentry.setUser(null);
}

// Set the current site context for multi-tenant tracking
export function setSiteContext(site: { id: string; name: string; url: string }) {
  Sentry.setTag('site_id', site.id);
  Sentry.setTag('site_name', site.name);
  Sentry.setContext('site', site);
}

// Track user actions as breadcrumbs
export function trackAction(action: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category: 'user-action',
    message: action,
    data,
    level: 'info',
  });
}

// Track navigation
export function trackNavigation(from: string, to: string) {
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Navigated from ${from} to ${to}`,
    data: { from, to },
    level: 'info',
  });
}

// Track API calls
export function trackApiCall(method: string, url: string, status?: number, duration?: number) {
  Sentry.addBreadcrumb({
    category: 'api',
    message: `${method} ${url}`,
    data: { method, url, status, duration },
    level: status && status >= 400 ? 'error' : 'info',
  });
}

// Capture error with extra context
export function captureError(error: Error, context?: Record<string, unknown>) {
  if (context) {
    Sentry.setContext('error_context', context);
  }
  Sentry.captureException(error);
}

// Capture message
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  Sentry.captureMessage(message, level);
}

// Start a transaction for performance monitoring
export function startTransaction(name: string, op: string) {
  return Sentry.startInactiveSpan({ name, op });
}

// Create feedback button
export function showFeedback() {
  const feedback = Sentry.getFeedback();
  if (feedback) {
    feedback.createWidget();
  }
}

// Export Sentry for direct usage
export { Sentry };
